import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import formatISODate from '../../../Helpers/formatISODate'
import './styles.scss'

const STEPS = [
  { label: 'Order Received', icon: solid('clipboard-list') },
  { label: 'Picked Up', icon: solid('box') },
  { label: 'In Transit', icon: solid('truck') },
  { label: 'Arrived', icon: solid('warehouse') },
  { label: 'Delivered', icon: solid('circle-check') },
]

const STATUS_STEP = {
  Inactive: 0,
  'Picked Up': 1,
  Active: 2,
  'On hold': 2,
  Arrived: 3,
  Delivered: 4,
}

const STATUS_CONFIG = {
  Active: { label: 'In Transit', color: '#1a44cc', bg: '#eef2ff', border: '#c7d2fe' },
  Inactive: { label: 'Pending', color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' },
  'Picked Up': { label: 'Picked Up', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  Arrived: { label: 'Arrived', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  Delivered: { label: 'Delivered', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  'On hold': { label: 'On Hold', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
}

const InfoRow = ({ icon, label, value }) => {
  if (!value) return null
  return (
    <div className="info-row">
      <div className="info-row__icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="info-row__body">
        <span className="info-row__label">{label}</span>
        <span className="info-row__value">{value}</span>
      </div>
    </div>
  )
}

const TrackItResult = ({ trackItResult, onReset }) => {
  const status = trackItResult.status
  const stepIndex = STATUS_STEP[status] ?? 0
  const statusConfig = STATUS_CONFIG[status] ?? STATUS_CONFIG.Inactive
  const isOnHold = status === 'On hold'

  const formatDate = (d) => formatISODate(d)?.split(' ')[0] || '—'

  return (
    <div className="track-result">

      {/* Back button */}
      <button className="track-result__back" onClick={onReset}>
        <FontAwesomeIcon icon={solid('arrow-left')} />
        Track another package
      </button>

      {/* Status header card */}
      <div className="track-result__header">
        <div className="track-result__header__left">
          <span className="track-result__header__eyebrow">Tracking Number</span>
          <h2 className="track-result__header__number">{trackItResult.trackingNumber}</h2>
          {trackItResult.packageDescription && (
            <p className="track-result__header__desc">{trackItResult.packageDescription}</p>
          )}
        </div>
        <div
          className="track-result__header__badge"
          style={{ color: statusConfig.color, backgroundColor: statusConfig.bg, borderColor: statusConfig.border }}
        >
          <span className="badge-dot" style={{ backgroundColor: statusConfig.color }} />
          {statusConfig.label}
        </div>
      </div>

      {/* Progress stepper */}
      <div className="track-result__stepper">
        {STEPS.map((step, idx) => {
          const completed = idx < stepIndex
          const active = idx === stepIndex
          return (
            <div key={idx} className="stepper-item">
              <div className={`stepper-item__track ${idx < STEPS.length - 1 ? 'has-line' : ''}`}>
                <div
                  className={`stepper-item__circle ${completed ? 'completed' : ''} ${active ? (isOnHold ? 'on-hold' : 'active') : ''}`}
                >
                  {completed
                    ? <FontAwesomeIcon icon={solid('check')} />
                    : <FontAwesomeIcon icon={step.icon} />
                  }
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`stepper-item__line ${completed ? 'completed' : ''}`} />
                )}
              </div>
              <span className={`stepper-item__label ${active ? 'active' : ''} ${completed ? 'completed' : ''}`}>
                {active && isOnHold ? 'On Hold' : step.label}
              </span>
            </div>
          )
        })}
      </div>

      {/* Main content grid */}
      <div className="track-result__grid">

        {/* Shipment details */}
        <div className="track-card">
          <div className="track-card__header">
            <FontAwesomeIcon icon={solid('box')} />
            <span>Shipment Details</span>
          </div>
          <div className="track-card__body">
            <InfoRow icon={solid('location-dot')} label="Current Location" value={trackItResult.currentLocation} />
            <InfoRow icon={solid('calendar-days')} label="Dispatch Date" value={formatDate(trackItResult.dispatchDate)} />
            <InfoRow icon={solid('calendar-check')} label="Estimated Delivery" value={formatDate(trackItResult.estimatedDeliveryDate)} />
            <InfoRow icon={solid('box-open')} label="Description" value={trackItResult.packageDescription} />
          </div>
        </div>

        {/* Sender + Receiver column */}
        <div className="track-result__people">

          <div className="track-card">
            <div className="track-card__header">
              <FontAwesomeIcon icon={solid('user')} />
              <span>Sender</span>
            </div>
            <div className="track-card__body">
              <InfoRow icon={solid('user')} label="Name" value={trackItResult.senderName} />
            </div>
          </div>

          <div className="track-card">
            <div className="track-card__header">
              <FontAwesomeIcon icon={solid('user-check')} />
              <span>Recipient</span>
            </div>
            <div className="track-card__body">
              <InfoRow icon={solid('user')} label="Name" value={trackItResult.receiverName} />
              <InfoRow icon={solid('location-dot')} label="Address" value={trackItResult.receiverAddress} />
            </div>
          </div>

        </div>
      </div>

      {/* Map */}
      {trackItResult.currentLocation && (
        <div className="track-card">
          <div className="track-card__header">
            <FontAwesomeIcon icon={solid('map-location-dot')} />
            <span>Package Location</span>
          </div>
          <div className="track-map">
            <iframe
              title="Package Location"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(trackItResult.currentLocation)}&output=embed&z=11`}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      )}

    </div>
  )
}

export default TrackItResult
